import { type ComputedRef, type InjectionKey, computed, ref } from "vue";
import { useStore } from "vuex";

export type MediaAssetType = "photo" | "video" | "document";

interface SelectableItem {
  id: number;
  customer_visible?: boolean;
  caption?: string | null;
}

interface Section {
  type: MediaAssetType;
  getItems: () => SelectableItem[];
}

type IdsByType = Record<MediaAssetType, number[]>;

export interface MediaSelection {
  count: ComputedRef<number>;
  counts: ComputedRef<Record<MediaAssetType, number>>;
  isSelected: (type: MediaAssetType, id: number) => boolean;
  toggle: (type: MediaAssetType, id: number) => void;
  deselect: (type: MediaAssetType, id: number) => void;
  selectAll: () => void;
  clear: () => void;
  registerSection: (section: Section) => void;
  unregisterSection: (type: MediaAssetType) => void;
  applyVisibility: (value: boolean) => Promise<void>;
  applyCaption: (caption: string) => Promise<void>;
}

export const MEDIA_SELECTION_KEY: InjectionKey<MediaSelection> =
  Symbol("mediaSelection");

/**
 * One selection shared across the photo, video, and document galleries on the
 * Media tab. Each gallery registers itself so a single "Select all" spans every
 * section and a single bulk action can fan out to all three endpoints. Keys are
 * `${type}:${id}` so ids from different tables never collide.
 */
export function useMediaSelection(jobId: () => number | null): MediaSelection {
  const store = useStore();
  const selectedKeys = ref<Set<string>>(new Set());
  const sections = new Map<MediaAssetType, Section>();

  const keyFor = (type: MediaAssetType, id: number) => `${type}:${id}`;
  const count = computed(() => selectedKeys.value.size);
  const counts = computed<Record<MediaAssetType, number>>(() => {
    const grouped = groupByType();
    return {
      photo: grouped.photo.length,
      video: grouped.video.length,
      document: grouped.document.length
    };
  });

  function isSelected(type: MediaAssetType, id: number): boolean {
    return selectedKeys.value.has(keyFor(type, id));
  }

  function toggle(type: MediaAssetType, id: number) {
    const key = keyFor(type, id);
    if (selectedKeys.value.has(key)) {
      selectedKeys.value.delete(key);
    } else {
      selectedKeys.value.add(key);
    }
  }

  function deselect(type: MediaAssetType, id: number) {
    selectedKeys.value.delete(keyFor(type, id));
  }

  function selectAll() {
    sections.forEach((section) => {
      section
        .getItems()
        .forEach((item) =>
          selectedKeys.value.add(keyFor(section.type, item.id))
        );
    });
  }

  function clear() {
    selectedKeys.value.clear();
  }

  function registerSection(section: Section) {
    sections.set(section.type, section);
  }

  function unregisterSection(type: MediaAssetType) {
    sections.delete(type);
    // A section that unmounts (e.g. videos behind a feature flag) should not
    // leave dangling selections behind.
    [...selectedKeys.value]
      .filter((key) => key.startsWith(`${type}:`))
      .forEach((key) => selectedKeys.value.delete(key));
  }

  function groupByType(): IdsByType {
    const grouped: IdsByType = { photo: [], video: [], document: [] };
    selectedKeys.value.forEach((key) => {
      const [type, id] = key.split(":");
      grouped[type as MediaAssetType].push(Number(id));
    });
    return grouped;
  }

  // Reflect a succeeded write locally: mutate the matching items and drop their
  // selection. Done per type so a partial failure leaves only the failed type
  // selected, with the rest of the UI already matching the server.
  function applyToType(
    type: MediaAssetType,
    ids: number[],
    mutate: (item: SelectableItem) => void
  ) {
    const idSet = new Set(ids);
    sections
      .get(type)
      ?.getItems()
      .forEach((item) => {
        if (idSet.has(item.id)) mutate(item);
      });
    ids.forEach((id) => deselect(type, id));
  }

  // Fan a bulk write out across the selected types: one request each, settle
  // them all, apply only the successes locally, and surface any partial failure
  // so the caller can notify. Applies immediately — the toolbar's visible count
  // is the confirmation, so there's no extra modal step. Keeps in-memory state
  // consistent with the server even if one type's request fails.
  async function applyBulk(
    label: string,
    action: string,
    allowedTypes: MediaAssetType[],
    payload: Record<string, unknown>,
    mutate: (item: SelectableItem) => void
  ): Promise<void> {
    const grouped = groupByType();
    const types = allowedTypes.filter((type) => grouped[type].length > 0);
    if (types.length === 0) return;

    const settled = await Promise.allSettled(
      types.map((type) =>
        store.dispatch(action, {
          jobId: jobId(),
          type,
          ids: grouped[type],
          ...payload
        })
      )
    );

    const failed: MediaAssetType[] = [];
    settled.forEach((outcome, index) => {
      const type = types[index];
      if (outcome.status === "fulfilled") {
        applyToType(type, grouped[type], mutate);
      } else {
        failed.push(type);
      }
    });

    if (failed.length > 0) {
      throw new Error(`Bulk ${label} update failed for: ${failed.join(", ")}`);
    }
  }

  function applyVisibility(value: boolean): Promise<void> {
    return applyBulk(
      "visibility",
      "bulkUpdateAssetVisibility",
      ["photo", "video", "document"],
      { customer_visible: value },
      (item) => {
        item.customer_visible = value;
      }
    );
  }

  // Documents have no caption column, so they're excluded from the type list.
  function applyCaption(caption: string): Promise<void> {
    return applyBulk(
      "caption",
      "bulkUpdateAssetCaption",
      ["photo", "video"],
      { caption },
      (item) => {
        item.caption = caption;
      }
    );
  }

  return {
    count,
    counts,
    isSelected,
    toggle,
    deselect,
    selectAll,
    clear,
    registerSection,
    unregisterSection,
    applyVisibility,
    applyCaption
  };
}
